import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { ApplicationNeighbors, applicationsApi } from "api/applications";

/**
 * Position and previous/next ids of an application in the list it was opened
 * from (BSCPF-6998), plus `listQuery`: the list query those neighbors come
 * from, undefined for the default Applications list.
 *
 * The saved list is the applications list query (minus paging) in router
 * state, set by the list's row open and forwarded by next/previous; a page
 * opened any other way (link, search, notification) has none and uses the
 * default list. So does an application that is not in the saved list; the
 * walk then continues on the default list. A failed saved-list request stays
 * an error (the navigator hides) rather than moving the walk to another list.
 *
 * Fetched once per application and never refetched: after a decision moves #4
 * out of the Submitted tab, Next must still lead to the old #5.
 */
export const useApplicationNeighbors = (applicationId: string) => {
  const listQuery = (
    useLocation().state as { applicationsListQuery?: string } | null
  )?.applicationsListQuery;

  return useQuery({
    queryKey: ["APPLICATION_NEIGHBORS", applicationId, listQuery],
    queryFn: async (): Promise<
      ApplicationNeighbors & { listQuery?: string }
    > => {
      if (listQuery) {
        const neighbors = await applicationsApi.getApplicationNeighbors(
          applicationId,
          listQuery
        );
        if (neighbors.position !== null) return { ...neighbors, listQuery };
      }

      return applicationsApi.getApplicationNeighbors(applicationId);
    },
    enabled: Boolean(applicationId),
    staleTime: Infinity
  });
};
